/**
 * Centralized Scratch Card Configurations
 * 
 * This file contains all configurations for the different scratch card types.
 * It serves as a single source of truth for card properties.
 */

// Quick Cash Configuration
export const QUICK_CASH_CONFIG = {
  id: 'quick_cash',
  name: 'QUICK CASH',
  description: 'Win up to 100,000 JINU!',
  cost: 200, // Cost set to 200 JINU
  maxWin: 100000,
  bgColor: 'blue.900', // Blue background
  accentColor: 'blue.400',
  cardBackground: 'rgba(0, 15, 30, 0.95)', // Match control panels background
  creator: 'PALMHEIST',
  
  // Grid configuration
  gridSize: {
    rows: 3,
    columns: 3
  },
  
  // Prize structure - updated to match the exact prize values
  prizes: ['100', '200', '300', '400', '500', '1K', '2K', '5K', '10K', '25K', '50K', '100K'],
  winningSymbols: 3,
  symbolType: 'star', // Default symbol type
  
  // Game mechanics - updated with exact probability
  winProbability: 0.4632, // 4,632 winners out of 10,000 cards
  
  // Bounty pool configuration - updated with detailed prize structure
  bountyPool: {
    totalCards: 10000,
    totalSales: 2000000, // 10,000 cards × 200 JINU = 2,000,000 JINU
    totalPrizePool: 1500000, // 1,500,000 JINU (75% RTP)
    minPrize: 100, // Minimum prize is 100 JINU
    
    // Prize tiers with exact counts from the provided structure
    tiers: [
      { name: 'Top', prizes: [
        { amount: 100000, count: 1, entries: 100 },  // 1 card wins 100,000 JINU
        { amount: 50000, count: 2, entries: 50 },    // 2 cards win 50,000 JINU
        { amount: 25000, count: 4, entries: 25 },    // 4 cards win 25,000 JINU
        { amount: 10000, count: 5, entries: 15 }     // 5 cards win 10,000 JINU
      ]},
      { name: 'Mid', prizes: [
        { amount: 5000, count: 10, entries: 10 },    // 10 cards win 5,000 JINU
        { amount: 2000, count: 20, entries: 8 },     // 20 cards win 2,000 JINU
        { amount: 1000, count: 40, entries: 5 },     // 40 cards win 1,000 JINU
        { amount: 500, count: 100, entries: 3 },     // 100 cards win 500 JINU
        { amount: 400, count: 100, entries: 3 },     // 100 cards win 400 JINU
        { amount: 300, count: 150, entries: 2 }      // 150 cards win 300 JINU
      ]},
      { name: 'Low', prizes: [
        { amount: 200, count: 3000, entries: 1 },    // 1,000 + 2,000 cards win 200 JINU
        { amount: 100, count: 1200, entries: 1 }     // 500 + 700 cards win 100 JINU
      ]}
    ],
    
    // Losing tickets get lottery entries
    losingTickets: {
      count: 5368, // 10,000 - 4,632 = 5,368 non-winning cards
      lotteryEntries: { min: 1, max: 5 } // Each losing card gets 1-5 lottery tickets randomly
    },
    
    // Summary statistics for display
    prizeSummary: {
      totalWinners: 4632,
      topPrizes: { 
        count: 12,                // 1 + 2 + 4 + 5 = 12 cards
        amount: 350000,          // 100K + 2×50K + 4×25K + 5×10K = 350,000 JINU
        entries: 12 * 10         // Average 10 entries per top prize
      },
      midPrizes: { 
        count: 420,               // 10 + 20 + 40 + 100 + 100 + 150 = 420 cards
        amount: 265000,          // 10×5K + 20×2K + 40×1K + 100×500 + 100×400 + 150×300 = 265,000 JINU
        entries: 420 * 5         // Average 5 entries per mid prize
      },
      lowPrizes: { 
        count: 4200,              // 3,000 + 1,200 = 4,200 cards
        amount: 720000,          // 3,000×200 + 1,200×100 = 720,000 JINU
        entries: 4200 * 1        // 1 entry per low prize
      },
      odds: {
        anyWin: '1 in 2.15',     // 10,000 ÷ 4,632 = 2.15
        topPrize: '1 in 10,000'  // Odds of winning 100K prize
      }
    }
  },
  
  // Visual elements
  coverImage: '/assets/images/scratch-cards/777-jackpot.png'
};

// Cash King Configuration
export const CASH_KING_CONFIG = {
  id: 'cash_king',
  name: 'CASH KING',
  description: 'Win up to 250,000 JINU!',
  cost: 500, // Updated cost to 500 JINU
  maxWin: 250000, // Updated max win to 250,000 JINU
  bgColor: 'green.800', // Green background
  accentColor: 'green.400',
  creator: 'PALMHEIST',
  
  // Grid configuration
  gridSize: {
    rows: 3,
    columns: 4
  },
  
  // Prize structure - updated to match the exact prize values
  prizes: ['100', '200', '300', '400', '500', '1K', '2K', '5K', '10K', '25K', '50K', '75K', '125K', '250K'],
  winningSymbols: 4,
  symbolType: 'diamond', // Diamond symbol type
  
  // Game mechanics - updated with exact probability
  winProbability: 0.4511, // 4,511 winners out of 10,000 cards
  
  // Bounty pool configuration - updated with detailed prize structure
  bountyPool: {
    totalCards: 10000,
    totalSales: 5000000, // 10,000 cards × 500 JINU = 5,000,000 JINU
    totalPrizePool: 3750000, // 3,750,000 JINU (75% RTP)
    minPrize: 100, // Minimum prize is 100 JINU
    
    // Prize tiers with exact counts from the provided structure
    tiers: [
      { name: 'Top', prizes: [
        { amount: 250000, count: 1, entries: 100 },  // 1 card wins 250,000 JINU
        { amount: 125000, count: 2, entries: 50 },   // 2 cards win 125,000 JINU
        { amount: 75000, count: 3, entries: 30 },    // 3 cards win 75,000 JINU
        { amount: 50000, count: 5, entries: 20 }     // 5 cards win 50,000 JINU
      ]},
      { name: 'Mid', prizes: [
        { amount: 25000, count: 10, entries: 15 },    // 10 cards win 25,000 JINU
        { amount: 10000, count: 20, entries: 10 },    // 20 cards win 10,000 JINU
        { amount: 5000, count: 30, entries: 8 },      // 30 cards win 5,000 JINU
        { amount: 2000, count: 40, entries: 5 },      // 40 cards win 2,000 JINU
        { amount: 1000, count: 100, entries: 3 }      // 100 cards win 1,000 JINU
      ]},
      { name: 'Lower', prizes: [
        { amount: 500, count: 500, entries: 2 },      // 500 cards win 500 JINU
        { amount: 400, count: 500, entries: 2 },      // 500 cards win 400 JINU
        { amount: 300, count: 1000, entries: 1 }      // 1,000 cards win 300 JINU
      ]},
      { name: 'Filler', prizes: [
        { amount: 200, count: 1200, entries: 1 },     // 1,200 cards win 200 JINU
        { amount: 100, count: 1100, entries: 1 }      // 1,100 cards win 100 JINU
      ]}
    ],
    
    // Losing tickets get lottery entries
    losingTickets: {
      count: 5489, // 10,000 - 4,511 = 5,489 non-winning cards
      lotteryEntries: { min: 1, max: 5 } // Each losing card gets 1-5 lottery tickets randomly
    },
    
    // Summary statistics for display
    prizeSummary: {
      totalWinners: 4511,
      topPrizes: { 
        count: 11,                // 1 + 2 + 3 + 5 = 11 cards
        amount: 925000,          // 250K + 2×125K + 3×75K + 5×50K = 925,000 JINU
        entries: 11 * 50         // Average 50 entries per top prize
      },
      midPrizes: { 
        count: 200,               // 10 + 20 + 30 + 40 + 100 = 200 cards
        amount: 730000,          // 10×25K + 20×10K + 30×5K + 40×2K + 100×1K = 730,000 JINU
        entries: 200 * 8         // Average 8 entries per mid prize
      },
      lowerPrizes: { 
        count: 2000,              // 500 + 500 + 1,000 = 2,000 cards
        amount: 1200000,         // 500×500 + 500×400 + 1,000×300 = 1,200,000 JINU
        entries: 2000 * 1.5      // Average 1.5 entries per lower prize
      },
      fillerPrizes: { 
        count: 2300,              // 1,200 + 1,100 = 2,300 cards
        amount: 895000,          // 1,200×200 + 1,100×100 = 895,000 JINU
        entries: 2300 * 1        // 1 entry per filler prize
      },
      odds: {
        anyWin: '1 in 2.2',       // 10,000 ÷ 4,511 = 2.2
        topPrize: '1 in 10,000'   // Odds of winning 250K prize
      }
    }
  },
  
  // Visual elements
  coverImage: '/assets/images/scratch-cards/777-jackpot.png'
};

// Jackpot Millions Configuration
export const JACKPOT_MILLIONS_CONFIG = {
  id: 'jackpot_millions',
  name: 'JACKPOT MILLIONS',
  description: 'Win up to 250,000 JINU!',
  cost: 500,
  maxWin: 250000,
  bgColor: 'purple.800', // Purple background
  accentColor: 'purple.400',
  
  // Grid configuration
  gridSize: {
    rows: 4,
    columns: 4
  },
  
  // Prize structure
  prizes: ['1K', '5K', '10K', '50K', '100K', '250K'],
  winningSymbols: 5,
  symbolType: 'crown', // Crown symbol type
  
  // Game mechanics
  winProbability: 0.2,
  
  // Visual elements
  coverImage: '/assets/images/scratch-cards/777-jackpot.png'
};

// Mega Millions Configuration
export const MEGA_MILLIONS_CONFIG = {
  id: 'mega_millions',
  name: 'MEGA MILLIONS',
  description: 'Win up to 100,000,000 JINU!',
  cost: 15000,
  maxWin: 100000000,
  bgColor: '#8B0000', // Dark red background
  accentColor: '#DC143C', // Crimson accent
  creator: 'PALMHEIST',
  
  // Grid configuration
  gridSize: {
    rows: 4,
    columns: 5
  },
  
  // Prize structure - with much higher values
  prizes: ['10K', '50K', '100K', '500K', '1M', '5M', '10M', '50M', '100M'],
  winningSymbols: 5,
  symbolType: 'gift', // Gift symbol type
  
  // Game mechanics
  winProbability: 0.12, // Lower probability for higher prizes
  
  // Visual elements
  coverImage: '/assets/images/scratch-cards/777-jackpot.png'
};

// Keep the old config for backward compatibility
export const QUICK_CASH_PREMIUM_CONFIG = MEGA_MILLIONS_CONFIG;

// Export all configurations as an array for easy access
export const ALL_CARD_CONFIGS = [
  MEGA_MILLIONS_CONFIG,
  CASH_KING_CONFIG,
  JACKPOT_MILLIONS_CONFIG,
  QUICK_CASH_PREMIUM_CONFIG
];

// Helper function to get a card config by ID
export const getCardConfigById = (id) => {
  // Normalize the ID to ensure consistent comparison
  const normalizedId = String(id).toLowerCase().trim();
  
  // Log for debugging
  console.log('getCardConfigById - looking for ID:', normalizedId);
  console.log('Available card IDs:', ALL_CARD_CONFIGS.map(c => c.id));
  
  // Find the card config by normalized ID
  const foundConfig = ALL_CARD_CONFIGS.find(config => config.id.toLowerCase() === normalizedId);
  
  if (foundConfig) {
    console.log('Found card config:', foundConfig.id);
    return foundConfig;
  }
  
  // Special case for Cash King
  if (normalizedId === 'cash_king') {
    console.log('Special case for Cash King');
    return CASH_KING_CONFIG;
  }
  
  // Default to Quick Cash if no match found
  console.log('No card config found, defaulting to Quick Cash');
  return QUICK_CASH_CONFIG;
};
